// apps/web/src/pages/Profile/components/PersonSection.tsx

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useMyPerson, useUpdateMyPerson } from '@alamo/api';
import type { MyPerson, UpdateMyPersonInput } from '@alamo/types';
import { Button, DateInput, Input, Spinner } from '@alamo/ui';
import { extractApiError } from '@alamo/utils';

const personSchema = z.object({
  fullName: z.string().min(1, 'common:validation.required').max(255),
  rg: z.string().max(20).optional(),
  birthDate: z.string().optional(),
});

type PersonFormValues = z.infer<typeof personSchema>;

/**
 * Seção "Dados de identidade" do perfil self-service (consultor PF/CLT).
 *
 * Gate de visibilidade = GET /profile/person retornar 200. O 404 é estado
 * esperado (party organization/PJ sem row em persons) e esconde a seção.
 * NUNCA gatear por consultantType — mesmo padrão de OrganizationSection.
 *
 * Distinta de profile.sections.personal (nome de exibição em `users.name`,
 * editado no form principal de ProfilePage) — aqui é o dado de identidade
 * legal (`persons.fullName`/`rg`/`birthDate`) usado na análise de cadastro.
 */
export function PersonSection() {
  const { data: person } = useMyPerson();

  if (!person) return null;

  return <PersonForm person={person} />;
}

function PersonForm({ person }: { person: MyPerson }) {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const update = useUpdateMyPerson();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<PersonFormValues>({
    resolver: zodResolver(personSchema),
    values: {
      fullName: person.fullName,
      rg: person.rg ?? '',
      birthDate: person.birthDate ?? '',
    },
  });

  const displayCpf = person.cpfLast4 ? `***.***.***-${person.cpfLast4}` : '—';

  const onSubmit = (values: PersonFormValues) => {
    const payload: UpdateMyPersonInput = {
      fullName: values.fullName,
      rg: values.rg || undefined,
      birthDate: values.birthDate || undefined,
    };

    update.mutate(payload, {
      onSuccess: () => {
        toast.success(t('auth:profile.person.toast.updated'));
      },
      onError: (err: unknown) => {
        const { code, message } = extractApiError(err);
        toast.error(
          t(`errors:${code ?? 'generic'}`, {
            defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
          }),
        );
      },
    });
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-heading font-bold text-secondary-950 mb-4">
        {t('auth:profile.sections.person')}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label={t('auth:profile.person.fields.cpf')}
          value={displayCpf}
          disabled
          readOnly
          helperText={t('auth:profile.person.helpers.cpf_readonly')}
        />
        <Input
          label={t('auth:profile.person.fields.full_name')}
          required
          {...register('fullName')}
          error={errors.fullName ? t(errors.fullName.message as string) : undefined}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={t('auth:profile.person.fields.rg')}
            {...register('rg')}
            error={errors.rg ? t(errors.rg.message as string) : undefined}
          />
          <Controller
            name="birthDate"
            control={control}
            render={({ field }) => (
              <DateInput
                label={t('auth:profile.person.fields.birth_date')}
                value={field.value || ''}
                onChange={(iso) => field.onChange(iso || '')}
                error={errors.birthDate?.message ? t(errors.birthDate.message) : undefined}
              />
            )}
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            className="min-h-[44px]"
            disabled={!isDirty || update.isPending}
            aria-busy={update.isPending || undefined}
          >
            {update.isPending && <Spinner size="sm" />}
            {t('auth:profile.person.actions.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
